"use client";

import { useMemo, useState } from "react";
import { Button, InputField } from "@vionixasia/shared-components";
import { AdminShell } from "@/components/AdminShell";
import { apiClient } from "@/lib/api-client";
import { useQueryParam } from "@/lib/use-query-param";
import type { ResultMapping, ValidatedImportRow } from "@/types/result";

const fields: { key: keyof ResultMapping; label: string; required?: boolean }[] = [
  { key: "runner_name", label: "Runner Name", required: true },
  { key: "phone_number", label: "Phone Number", required: true },
  { key: "finish_time", label: "Finish Time", required: true },
  { key: "bib_number", label: "BIB" },
  { key: "category", label: "Category" },
  { key: "rank", label: "Rank" },
  { key: "gender", label: "Gender" },
  { key: "nationality", label: "Nationality" },
  { key: "result_status", label: "Result Status" },
];

export default function ImportPage() {
  const projectId = useQueryParam("projectId");
  const eventId = useQueryParam("eventId");
  const [file, setFile] = useState<File | null>(null);
  const [columns, setColumns] = useState<string[]>([]);
  const [rows, setRows] = useState<Record<string, unknown>[]>([]);
  const [preview, setPreview] = useState<Record<string, unknown>[]>([]);
  const [mapping, setMapping] = useState<Partial<ResultMapping>>({});
  const [validated, setValidated] = useState<ValidatedImportRow[]>([]);
  const [message, setMessage] = useState("");

  const basePath = `/api/projects/${projectId}/events/${eventId}`;
  const summary = useMemo(() => {
    const issues = validated.flatMap((row) => row.issues);
    return {
      rows: validated.length,
      canSend: validated.filter((row) => row.canSendWhatsApp).length,
      errors: issues.filter((issue) => issue.severity === "error").length,
      warnings: issues.filter((issue) => issue.severity === "warning").length,
    };
  }, [validated]);

  async function parse() {
    if (!file || !projectId || !eventId) return;
    const form = new FormData();
    form.append("file", file);
    const data = await apiClient<{ columns: string[]; rows: Record<string, unknown>[]; preview_rows: Record<string, unknown>[] }>(
      `${basePath}/import/parse`,
      { method: "POST", body: form }
    );
    setColumns(data.columns);
    setRows(data.rows);
    setPreview(data.preview_rows);
    const auto: Partial<ResultMapping> = {};
    fields.forEach((field) => {
      const match = data.columns.find((column) => column.toLowerCase().replace(/[^a-z]/g, "").includes(field.key.replace("_", "")));
      if (match) auto[field.key] = match;
    });
    setMapping(auto);
    setValidated([]);
  }

  async function validate() {
    const data = await apiClient<{ rows: ValidatedImportRow[] }>(`${basePath}/import/save`, {
      method: "POST",
      body: JSON.stringify({ mode: "validate", mapping, rows, file_name: file?.name || "result.xlsx" }),
    });
    setValidated(data.rows);
  }

  async function save() {
    const data = await apiClient<{ saved: number }>(`${basePath}/import/save`, {
      method: "POST",
      body: JSON.stringify({ mode: "save", mapping, rows, file_name: file?.name || "result.xlsx" }),
    });
    setMessage(`${data.saved} result berhasil disimpan.`);
  }

  return (
    <AdminShell>
      <h1 className="text-2xl font-semibold text-ink">Import Result</h1>
      <div className="mt-6 rounded-card border border-line bg-white p-5">
        <div className="flex flex-wrap items-end gap-3">
          <InputField wrapperClassName="min-w-[260px]" label="Excel file" name="file" type="file" accept=".xlsx" onChange={(e) => setFile(e.target.files?.[0] || null)} labelColor="text-ink" />
          <Button color="teal" disabled={!file || !projectId || !eventId} onClick={parse}>Upload & Preview</Button>
        </div>
      </div>

      {columns.length > 0 && (
        <div className="mt-6 grid gap-6 lg:grid-cols-[360px_1fr]">
          <section className="rounded-card border border-line bg-white p-5">
            <h2 className="font-semibold text-ink">Column Mapping</h2>
            <div className="mt-4 space-y-3">
              {fields.map((field) => (
                <InputField
                  key={field.key}
                  label={field.label}
                  required={field.required}
                  name={field.key}
                  type="select"
                  value={mapping[field.key] || ""}
                  onChange={(e) => setMapping((prev) => ({ ...prev, [field.key]: e.target.value }))}
                  labelColor="text-ink"
                  defaultOptionLabel={null}
                >
                    <option value="">Not mapped</option>
                    {columns.map((column) => <option key={column} value={column}>{column}</option>)}
                </InputField>
              ))}
            </div>
            <div className="mt-5 flex gap-2">
              <Button variant="muted" onClick={validate}>Validate</Button>
              <Button color="teal" disabled={validated.length === 0} onClick={save}>Save Result</Button>
            </div>
            {validated.length > 0 && <div className="mt-4 rounded bg-surface p-3 text-sm text-muted">Rows: {summary.rows} · Can send: {summary.canSend} · Errors: {summary.errors} · Warnings: {summary.warnings}</div>}
            {message && <p className="mt-4 rounded bg-emerald-50 p-3 text-sm text-emerald-700">{message}</p>}
          </section>
          <section className="rounded-card border border-line bg-white p-5">
            <h2 className="font-semibold text-ink">Preview 20 Rows</h2>
            <div className="table-scroll mt-4">
              <table className="min-w-full text-left text-sm">
                <thead className="bg-surface text-muted"><tr>{columns.map((column) => <th key={column} className="px-3 py-2 font-medium">{column}</th>)}</tr></thead>
                <tbody>{preview.map((row, index) => <tr key={index} className="border-t border-line">{columns.map((column) => <td key={column} className="px-3 py-2">{String(row[column] ?? "")}</td>)}</tr>)}</tbody>
              </table>
            </div>
          </section>
        </div>
      )}
    </AdminShell>
  );
}
